import type {
  AgentProfile,
  AgentTrip,
  BookingCreatePayload,
  BookingPackage,
  ImpactProject,
  Operator,
  Review,
} from './types';

export const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

export function fetchWithAuth(input: RequestInfo, init: RequestInit = {}) {
  const token = localStorage.getItem('auth_token');
  const headers = new Headers(init.headers || {});
  if (token) headers.set('Authorization', `Bearer ${token}`);
  return fetch(input, { ...init, headers });
}

function wait(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

// In-memory mock DB, so UI updates persist across calls.
const db = {
  agent: {
    id: 'agent-1',
    name: 'Amina K. Mwangi',
    photoUrl: '/images/community/maasai-community-dance.jpg',
    heroImageUrl: '/images/places/ngorongoro-crater-lodge.jpg',
    location: 'Nairobi, Kenya',
    yearsOfExperience: 9,
    languages: ['English', 'Swahili', 'French'],
    specializations: ['Community-based safaris', 'Conservation-focused tours'],
    bio: 'I connect travelers with local partners who protect wildlife and strengthen communities.',
    philosophy:
      'Ethical tourism means transparency, fair wages, and protecting habitats. SafariCo Hub helps you see the impact behind every booking.',
    certifications: ['Fair Trade Tourism', 'Local NGO partnerships'],
  } satisfies AgentProfile,

  trips: [
    {
      id: 'trip-1',
      title: 'Elephants & Elders Journey',
      imageUrl: '/images/wildlife/elephant-family.jpg',
      destination: 'Amboseli (Community Lands)',
      dates: 'Jun 10-13, 2026',
      groupSize: 8,
      remainingSeats: 2,
      packageId: 'pkg-amboseli-1',
    },
    {
      id: 'trip-2',
      title: 'Coastal Reef Restoration',
      imageUrl: '/images/trips/safari-camp.jpg',
      destination: 'Mombasa (Marine Conservation)',
      dates: 'Jul 2-5, 2026',
      groupSize: 10,
      remainingSeats: 4,
      packageId: 'pkg-reef-1',
    },
  ] satisfies AgentTrip[],

  operators: [
    {
      id: 'op-1',
      name: 'Kifaru Community Safaris',
      imageUrl: '/images/wildlife/giraffe.jpg',
      region: 'Tanzania (Northern Circuit)',
      location: 'Arusha',
      languages: ['English', 'Swahili'],
      experienceType: 'Wildlife',
      communityOwnershipInfo:
        'Local co-operative ownership. Guides receive fair wages and community fees fund schools and water access.',
      impactHighlights: ['Education', 'Women-led', 'Conservation'],
      averageRating: 4.7,
      reviewCount: 186,
      groupPackages: [
        { id: 'pkg-amboseli-1', title: 'Amboseli Family Conservation', duration: '4 days', basePrice: 240 },
      ],
      reviews: [
        { id: 'r1', reviewerName: 'Maya', rating: 5, comment: 'Transparent and genuinely impactful.', dateISO: '2026-02-10' },
        { id: 'r2', reviewerName: 'Daniel', rating: 4, comment: 'Great guides, loved the community visits.', dateISO: '2026-01-24' },
      ] satisfies Review[],
    },
    {
      id: 'op-2',
      name: 'Sauti ya Kijiji Homestays',
      imageUrl: '/images/community/maasai-community-dance.jpg',
      region: 'Kenya (Coast)',
      location: 'Mombasa',
      languages: ['English', 'Swahili', 'French'],
      experienceType: 'Community homestay',
      communityOwnershipInfo:
        'Owned and run by local households. A portion of each booking supports youth-led cultural programs.',
      impactHighlights: ['Youth programs', 'Community education', 'Women-led'],
      averageRating: 4.6,
      reviewCount: 92,
      groupPackages: [
        { id: 'pkg-reef-1', title: 'Reef Restoration Workshop', duration: '4 days', basePrice: 210 },
      ],
      reviews: [
        { id: 'r3', reviewerName: 'Alana', rating: 5, comment: 'Authentic and easy to trust.', dateISO: '2026-03-02' },
      ] satisfies Review[],
    },
  ] satisfies Operator[],

  projects: [
    {
      id: 'proj-1',
      title: "School Scholarships for Rangers' Families",
      imageUrl: '/images/impact/community-beekeeping.jpg',
      location: 'Arusha Region',
      description: 'Scholarships that help children continue education while their families support conservation.',
      goalAmount: 25000,
      raisedAmount: 14680,
      impactMetrics: [
        { label: 'Scholarships funded', value: '18 students' },
        { label: 'Mentoring sessions', value: '36 sessions' },
      ],
      status: 'Ongoing',
    },
    {
      id: 'proj-2',
      title: 'Hectares Restored: Riparian Habitat Recovery',
      imageUrl: '/images/wildlife/rhinos.jpg',
      location: 'Coastal Wetlands',
      description: 'Community-led planting and monitoring to restore vital habitats for biodiversity.',
      goalAmount: 40000,
      raisedAmount: 30500,
      impactMetrics: [
        { label: 'Habitat restored', value: '24 hectares' },
        { label: 'Community jobs', value: '61 paid roles' },
      ],
      status: 'Funding phase',
    },
  ] satisfies ImpactProject[],

  packages: [
    {
      id: 'pkg-amboseli-1',
      title: 'Amboseli Family Conservation',
      imageUrl: '/images/wildlife/elephant-family.jpg',
      operatorName: 'Kifaru Community Safaris',
      destination: 'Amboseli (Community Lands)',
      startDate: '2026-06-10',
      endDate: '2026-06-13',
      totalSeats: 8,
      bookedSeats: 6,
      basePricePerSeat: 240,
      discountForLastSeat: 0.25,
    },
    {
      id: 'pkg-reef-1',
      title: 'Reef Restoration Workshop',
      imageUrl: '/images/trips/safari-camp.jpg',
      operatorName: 'Sauti ya Kijiji Homestays',
      destination: 'Mombasa (Marine Conservation)',
      startDate: '2026-07-02',
      endDate: '2026-07-05',
      totalSeats: 10,
      bookedSeats: 6,
      basePricePerSeat: 210,
      discountForLastSeat: 0.2,
    },
  ] satisfies BookingPackage[],
};

export const api = {
  async getAgentProfile(): Promise<AgentProfile> {
    await wait(450);
    return db.agent;
  },

  async getAgentTrips(): Promise<AgentTrip[]> {
    await wait(450);
    return db.trips;
  },

  async getOperators(): Promise<Operator[]> {
    await wait(500);
    return db.operators;
  },

  async getProjects(): Promise<ImpactProject[]> {
    await wait(450);
    return db.projects;
  },

  async donateToProject(projectId: string, payload: { amount: number; name?: string; email?: string }): Promise<void> {
    await wait(600);
    const proj = db.projects.find((p) => p.id === projectId);
    if (!proj) throw new Error('Project not found');
    proj.raisedAmount = Math.min(proj.goalAmount, proj.raisedAmount + payload.amount);
  },

  async getPackageById(packageId: string): Promise<BookingPackage> {
    await wait(450);
    const pkg = db.packages.find((p) => p.id === packageId);
    if (!pkg) throw new Error('Package not found');
    return pkg;
  },

  async createBooking(payload: BookingCreatePayload): Promise<{ voucherCode: string | null }> {
    await wait(800);
    const pkg = db.packages.find((p) => p.id === payload.packageId);
    if (!pkg) throw new Error('Package not found');

    const remainingBefore = pkg.totalSeats - pkg.bookedSeats;
    if (payload.seats < 1 || payload.seats > remainingBefore) {
      throw new Error('Selected seats are not available');
    }

    pkg.bookedSeats += payload.seats;

    const isSoloLastSeat = remainingBefore === 1 && payload.seats === 1;
    if (isSoloLastSeat) {
      return { voucherCode: 'SAFARI-IMPACT-2026' };
    }

    return { voucherCode: null };
  },
};
